/*
 * The bell: unread count and the latest notifications. Rendering of a notification (icon, title, body) is
 * shared with the notifications page through <NotificationRow>.
 */
import { Bell, CheckCircle, Info, Warning, WarningOctagon } from '@phosphor-icons/react'
import { Popover } from 'radix-ui'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '@/api/hooks/notifications'
import type { AppNotification } from '@/api/types'
import { Bidi, Button, EmptyState, ErrorState, SkeletonText } from '@/components/ui'
import { cn } from '@/lib/cn'
import { formatSarText } from '@/lib/format'
import { useFormat } from '@/i18n/useFormat'

const SEVERITY = {
  info: { Icon: Info, cls: 'bg-brand-50 text-brand-600' },
  success: { Icon: CheckCircle, cls: 'bg-teal-50 text-teal-600' },
  warning: { Icon: Warning, cls: 'bg-warm-50 text-warm-700' },
  danger: { Icon: WarningOctagon, cls: 'bg-danger-50 text-danger-600' },
}

/** Title and body of a notification in the current language; generic copy for a kind without translations. */
export function useNotificationText() {
  const { t, i18n } = useTranslation('notifications')
  const f = useFormat()
  return (n: AppNotification) => {
    if (!i18n.exists(`notifications:kinds.${n.kind}.title`))
      return { title: t('fallback.title'), body: t('fallback.body') }
    const params: Record<string, string | number | undefined> = { ...n.params }
    if (typeof n.params.amount === 'number') params.amount = formatSarText(n.params.amount, f.locale)
    if (typeof n.params.days === 'number') params.count = n.params.days
    return {
      title: t(`kinds.${n.kind}.title` as 'kinds.payment_failed.title', params),
      body: t(`kinds.${n.kind}.body` as 'kinds.payment_failed.body', params),
    }
  }
}

export function NotificationRow({
  n,
  onOpen,
  className,
}: {
  n: AppNotification
  onOpen?: () => void
  className?: string
}) {
  const f = useFormat()
  const text = useNotificationText()
  const { title, body } = text(n)
  const { Icon, cls } = SEVERITY[n.severity] ?? SEVERITY.info
  const inner = (
    <>
      <span className={cn('grid size-8 shrink-0 place-items-center rounded-xl', cls)} aria-hidden="true">
        <Icon className="size-[18px]" weight="fill" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className={cn('text-14 leading-5', n.readAt ? 'text-body' : 'font-semibold text-ink')}>
          <Bidi text={title} />
        </span>
        <span className="text-13 leading-5 text-muted">
          <Bidi text={body} />
        </span>
        <time dateTime={n.createdAt} title={f.date(n.createdAt, 'datetime')} className="text-12 text-subtle">
          {f.relative(n.createdAt)}
        </time>
      </span>
      {!n.readAt ? (
        <span className="mt-1.5 size-2 shrink-0 rounded-full bg-brand-600" aria-hidden="true" />
      ) : null}
    </>
  )
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'flex w-full items-start gap-3 rounded-xl p-2.5 text-start transition-colors hover:bg-surface-tint',
        className,
      )}
    >
      {inner}
    </button>
  )
}

export function NotificationsPopover() {
  const { t } = useTranslation('shell')
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const q = useNotifications({ pageSize: 8 })
  const read = useMarkNotificationRead()
  const readAll = useMarkAllNotificationsRead()
  const unread = q.data?.unread ?? 0
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        aria-label={unread ? t('notifications.labelUnread', { count: unread }) : t('notifications.label')}
        className="relative grid size-9 place-items-center rounded-xl text-muted transition-colors hover:bg-control hover:text-ink data-[state=open]:bg-control touch:size-11"
      >
        <Bell className="size-[19px]" aria-hidden="true" />
        {unread ? (
          <span className="absolute -end-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-danger-600 px-1 figures text-[10px] font-bold text-white ring-2 ring-app-canvas">
            {unread > 9 ? '9+' : unread}
          </span>
        ) : null}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          collisionPadding={12}
          className="z-50 flex max-h-[min(34rem,calc(100dvh-5rem))] w-[min(24rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-2xl bg-surface shadow-pop ring-1 ring-hairline data-[state=open]:animate-pop-in"
        >
          <div className="flex items-center justify-between gap-3 border-b border-hairline px-4 py-3">
            <h2 className="text-15 font-semibold text-ink">{t('notifications.title')}</h2>
            {unread ? (
              <Button
                variant="link"
                size="sm"
                className="text-13"
                onClick={() => readAll.mutate()}
                disabled={readAll.isPending}
              >
                {t('notifications.markAll')}
              </Button>
            ) : null}
          </div>
          <div className="min-h-0 flex-1 scrollbar-thin overflow-y-auto p-1.5">
            {q.isPending ? (
              <div className="flex flex-col gap-4 p-3">
                <SkeletonText lines={2} />
                <SkeletonText lines={2} />
              </div>
            ) : q.isError ? (
              <ErrorState error={q.error} onRetry={() => q.refetch()} compact />
            ) : q.data.items.length === 0 ? (
              <EmptyState compact icon={<Bell />} title={t('notifications.empty')} />
            ) : (
              q.data.items.map((n) => (
                <NotificationRow
                  key={n.id}
                  n={n}
                  onOpen={() => {
                    if (!n.readAt) read.mutate(n.id)
                    setOpen(false)
                    if (n.href) navigate(n.href)
                  }}
                />
              ))
            )}
          </div>
          <div className="border-t border-hairline p-1.5">
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="flex h-10 items-center justify-center rounded-xl text-13 font-semibold text-brand-700 transition-colors hover:bg-brand-50"
            >
              {t('notifications.viewAll')}
            </Link>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
